import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * A reusable Input component with Tailwind CSS styling.
 *
 * @component
 * @param {object} props - Props passed to the input element.
 * @param {string} [props.className] - Additional class names to customize styling.
 * @param {string} [props.type="text"] - The type of input (e.g., "text", "password", "email").
 * @param {...any} [props] - Any other standard HTML input attributes (value, onChange, placeholder, disabled, etc.).
 *
 * The field says where the keyboard is. It used to clear the browser's own
 * outline and put nothing in its place, which left every input in the app with
 * no focus state at all. `focus-visible` rather than `focus` so a checkbox or
 * a radio does not light up under the mouse; a text field matches it either
 * way.
 */
export const Input = ({ className, type = "text", ...props }) => {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        // `border-border-input`, not `border-border-primary`. On a white card the
        // field has no fill of its own, so the border is the only thing saying
        // the field is there, and #D6D9DE managed 1.18:1 against it.
        //
        // `placeholder:text-text-muted`, not `placeholder-text-text-muted`.
        // The second is not a class Tailwind generates — it never reached the
        // built CSS, so every Input in the app had no placeholder colour at all.
        "w-full bg-background-form/40 border border-border-input rounded-lg px-4 py-2.5 text-sm text-text-primary placeholder:text-text-muted transition",
        "focus:outline-none focus-visible:outline-none focus-visible:border-tone-blue focus-visible:ring-2 focus-visible:ring-tone-blue/40",
        // readonly styles
        type === "checkbox" && props?.readOnly ? "cursor-not-allowed opacity-50" : "",
        props?.readOnly && type !== "checkbox" ? "read-only:cursor-not-allowed read-only:bg-background-form/10" : "",
        className,
      )}
      {...props}
    />
  );
};
